import { useMemo } from "react";

import useAssistantPanelAnalytics from "components/AssistantPanel/analytics/useAnalytics";
import useAssistantPanelSendPrompt from "components/AssistantPanel/hooks/useSendPrompt";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import { LogoInfraAssistantColored } from "components/icons/generated";
import useIntelligenceSettings from "shared/Intelligence/useIntelligenceSettings";

import useCommandPaletteStore from "../store/useCommandPaletteStore";
import { detectSearchIntent } from "../utils/detectSearchIntent";
import { CommandPaletteAction, CommandPaletteActionType } from "../types";
import { BUCKET_IDS } from "../constants";

const ACTION_TITLE = "Ask Infra Assistant";

const useCommandPaletteAskInfraAssistantAction = (): CommandPaletteAction | undefined => {
  const { canUseInfraAssistant } = useIntelligenceSettings();
  const searchQuery = useCommandPaletteStore((state) => state.searchQuery);
  const { openPrompt, isChatOpen } = useAssistantPanelSendPrompt();
  const { trackConversationOpened } = useAssistantPanelAnalytics();
  const setMobileFullScreen = useMainNavigationStore((state) => state.setMobileFullScreen);

  return useMemo(() => {
    if (!canUseInfraAssistant) {
      return undefined;
    }

    const question = detectSearchIntent({ title: ACTION_TITLE }, searchQuery) ? searchQuery : "";

    return {
      type: CommandPaletteActionType.SIMPLE,
      id: "ask-infra-assistant",
      title: ACTION_TITLE,
      alwaysVisible: true,
      bucketId: BUCKET_IDS.ASK_INFRA_ASSISTANT,
      icon: LogoInfraAssistantColored,
      metadata: { title: question || undefined },
      onAction: () => {
        trackConversationOpened("command_palette");

        setMobileFullScreen(false);
        openPrompt(question, { newChat: !isChatOpen });
      },
    };
  }, [
    canUseInfraAssistant,
    searchQuery,
    openPrompt,
    isChatOpen,
    trackConversationOpened,
    setMobileFullScreen,
  ]);
};

export default useCommandPaletteAskInfraAssistantAction;
